import { Component, ElementRef, inject, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray } from '@angular/forms';
import { Router } from '@angular/router';

import { NgxSpinnerService } from 'ngx-spinner';

import { forkJoin } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import * as CookieConsent from 'vanilla-cookieconsent';

import { mapAsBody } from '../../../shared/util/request-util';

import { AccessConstraintEnum } from '../../../../models/enums/access-constraint.enum';
import { DisplayOfferModeEnum } from '../../../../models/enums/display-offer-mode.enum';
import { LoadingConstraintEnum } from '../../../../models/enums/loading-constraint.enum';
import { OfferType } from '../../../../models/enums/offer-type.enum';
import { TRANSPORT_ITEM_ESTIMATE_STEP } from '../../../../models/enums/transport-item-estimate-step.enum';

import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';

import { PATHS } from '../../../../models/bary-paths.model';
import { Constraint, OfferEstimation } from '../../../../models/offer-estimation.model';
import { IPricingTransporterConfiguration } from '../../../../models/pricing-transporter.configuration';
import { TransportItem } from '../../../../models/transport-item.model';
import { Transporter } from '../../../../models/transporter.model';
import { Transport } from '../../../../models/transport.model';

import { BaryFormFactoryService } from '../../../../services/bary-form-factory.service';
import { CurrentDataService } from '../../../../services/current-data-service';
import { BaryModalService } from '../../../../services/bary-modal.service';
import { EstimateService } from '../../../../services/estimate.service';
import { PricingTransporterConfigurationService } from '../../../../services/pricing-transporter-configuration.service';
import { TransportExtendedService } from '../../../../services/transport-extended.service';
import { TransportStep } from '../../../../models/enums/transport-step.enum';

@Component({
  selector: 'jhi-bary-light-estimate',
  templateUrl: './bary-light-estimate.component.html',
  styleUrls: ['bary-light-estimate.component.scss'],
})
export class BaryLightEstimateComponent implements OnInit {
  readonly #router = inject(Router);
  readonly #pricingTransporterConfigurationService = inject(PricingTransporterConfigurationService);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #transportService = inject(TransportExtendedService);
  readonly #bff = inject(BaryFormFactoryService);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #estimateService = inject(EstimateService);
  readonly #modalService = inject(BaryModalService);

  @ViewChild('card') card!: ElementRef;
  @ViewChildren('pricingPack') pricingPacks!: QueryList<ElementRef>;

  public spinnerPriceName = 'priceRefreshing';

  public loadingData = false;

  public quotationOK = false;

  public transportFormGroup!: TransportFormGroup;
  public transporter!: Transporter;

  public offerEstimations: OfferEstimation[] = [];
  public withoutConstraintsOfferEstimations: OfferEstimation[] = [];
  public quotationEstimation?: OfferEstimation;

  public displayOfferMode: DisplayOfferModeEnum = DisplayOfferModeEnum.VALID;
  public DisplayOfferModeEnum = DisplayOfferModeEnum;

  public noOfferWithConstraints?: string;
  public quotationEstimationMessage?: string;

  private _creatorUuid = CookieConsent.getCookie().consentId;

  public ngOnInit(): void {
    forkJoin({
      transport: this.#transportService.createEmpty(),
      transporter: this.#currentDataService.getCurrentTransporter(),
      customer: this.#currentDataService.getCustomer(),
      config: this.#pricingTransporterConfigurationService.retrieveConfiguration().pipe(mapAsBody),
    }).subscribe(data => {
      this.transporter = data.transporter;
      // build the formGroup
      this.transportFormGroup = this.#bff.makeTransportForm(data.config, data.transporter!, data.transport);
      // define refreshment conditions
      this.defineRefreshingConditions();
      this.refreshOffer();
    });
  }

  public chooseOffer(offer: OfferEstimation, withoutConstraint: boolean = false): void {
    this.updateTransport(offer, withoutConstraint);
  }

  public updateTransport(offer: OfferEstimation, withoutConstraint: boolean): void {
    const transport = new Transport({
      ...this.transportFormGroup.getAngularForm().getRawValue(),
      id: this.transportFormGroup.controls.id.value,
      creatorUuid: this._creatorUuid,
      orderTunnelStep: TransportStep.SEARCH_OFFERS,
    });

    if (withoutConstraint) {
      this.removeConstraints(transport, offer);
    }
    this.#transportService.applyOffer(offer.id, transport).subscribe((newTransport: Transport): void => {
      this.#pricingTransporterConfigurationService
        .retrieveConfiguration()
        .pipe(mapAsBody)
        .subscribe({
          next: (config: IPricingTransporterConfiguration): void => {
            this.transportFormGroup = this.#bff.makeTransportForm(config, this.transporter!, newTransport);
            this.#currentDataService.updateCurrentTransport(newTransport);
            this.#currentDataService.updateTransportInModification(undefined);
            this.#router.navigateByUrl(PATHS.ESTIMATE.path).then();
          },
        });
    });
  }

  // TODO: changer le type any de transportItemForm
  public getValueForTransportItem(transportItemForm: any): any {
    return transportItemForm.getRawValue();
  }

  private refreshOffer(): void {
    if (this.transportFormGroup.value() && this.transportFormGroup.controls.items.length) {
      const transport = new Transport(this.transportFormGroup.getAngularForm().getRawValue());
      transport.creatorUuid = this._creatorUuid;
      transport.orderTunnelStep = TransportStep.SEARCH_OFFERS;
      // check
      this.quotationOK = transport.checkItems() && transport.checkAddresses();
      if (!this.quotationOK) {
        this.#spinnerService.hide(this.spinnerPriceName).then((): boolean => (this.loadingData = false));
        return;
      }
      this.#spinnerService.show(this.spinnerPriceName).then((): boolean => (this.loadingData = true));
      this.doEstimatePricing(transport);
    }
  }

  public initTransportItem(index?: number): void {
    const transportItemFormValue: any = index || index === 0 ? this.transportFormGroup.controls.items[index].value : {};
    this.#modalService
      .openModalEstimate(this.transportFormGroup, this.currentStep, new TransportItem(transportItemFormValue))
      .action()
      .subscribe(res => {
        if (res.submit) {
          if (typeof index === 'number') {
            // MAJ du transportItem
            this.transportFormGroup.controls.items[index] = res.value;
            (this.transportFormGroup.getAngularForm().controls['items'] as FormArray).controls[index] = res.value;
            // Comme on modifie directement un élément dans le tableau, on ne déclenche pas la condition de rafraîchissement définie dans defineRefreshingConditions
            this.refreshOffer();
          } else {
            // Ajout du transportItem
            // Comme on pousse un élément dans le tableau, on déclenche la condition de rafraîchissement définie dans defineRefreshingConditions
            this.transportFormGroup.controls.items.push(res.value);
            (this.transportFormGroup.getAngularForm().controls['items'] as FormArray).push(res.value);
          }
        }
      });
  }

  private get currentStep(): TRANSPORT_ITEM_ESTIMATE_STEP {
    if (this.transportFormGroup.controls.isControlledTemperature.value || this.transportFormGroup.controls.isHazardousMaterial.value) {
      return TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL;
    }
    return this.transportFormGroup.controls.items.length > 0 ? TRANSPORT_ITEM_ESTIMATE_STEP.ITEM : TRANSPORT_ITEM_ESTIMATE_STEP.SETTING;
  }

  public handleRemove(index: number): void {
    this.transportFormGroup.removeTransportItem(index);
    if (this.transportFormGroup.controls.items.length === 0) {
      this.offerEstimations = [];
    }
    this.refreshOffer();
  }

  public openUseRegisteredAddress(isPickup: boolean): void {
    this.#modalService
      .openUseRegisteredAddress(this.transportFormGroup, isPickup, true)
      .action()
      .subscribe(action => {
        if (action.submit) {
          this.refreshOffer();
        }
      });
  }

  /**
   * Define refreshment conditions
   * @private
   */
  private defineRefreshingConditions(): void {
    this.transportFormGroup
      .getAngularForm()
      .valueChanges.pipe(debounceTime(300))
      .subscribe((): void => {
        this.refreshOffer();
      });
  }

  private doEstimatePricing(transport: Transport): void {
    this.quotationEstimation = undefined;
    this.offerEstimations = [];
    this.quotationEstimationMessage = undefined;
    this.noOfferWithConstraints = undefined;

    this.#estimateService
      .estimatePricing(transport)
      .pipe(mapAsBody)
      .subscribe((offerEstimations: OfferEstimation[]): void => {
        this.offerEstimations = offerEstimations || [];
        if (this.offerEstimations.length && this.offerEstimations[0].transport) {
          // tracking updateForm transport using back's transport
          this.transportFormGroup.updateForm(this.offerEstimations[0].transport, { emitEvent: false });
        }
        this.changeDisplayOfferMode();
        this.offerEstimations.sort((a, b) => Number(a.price) - Number(b.price));
        this.withoutConstraintsOfferEstimations.sort((a, b) => Number(a.price) - Number(b.price));
        this.card.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      })
      .add((): void => {
        this.#spinnerService.hide(this.spinnerPriceName).then(() => {
          this.loadingData = false;
        });
      });
  }

  private changeDisplayOfferMode(): void {
    const hasWithoutConstraints = this.offerEstimations.some(offer => offer.type === OfferType.WITHOUT_CONSTRAINTS);
    const hasValid = this.offerEstimations.some(offer => offer.type === OfferType.VALID);

    if (hasWithoutConstraints && hasValid) {
      this.displayOfferMode = DisplayOfferModeEnum.VALID_AND_WITHOUT_CONSTRAINTS;
      this.withoutConstraintsOfferEstimations = this.offerEstimations.filter(offer => offer.type === OfferType.WITHOUT_CONSTRAINTS);
      this.offerEstimations = this.offerEstimations.filter(offer => offer.type === OfferType.VALID);
    } else if (!hasWithoutConstraints) {
      this.displayOfferMode = DisplayOfferModeEnum.VALID;
    } else {
      this.displayOfferMode = DisplayOfferModeEnum.QUOTATION_AND_WITHOUT_CONSTRAINTS;
      this.withoutConstraintsOfferEstimations = this.offerEstimations.filter(offer => offer.type === OfferType.WITHOUT_CONSTRAINTS);
      this.quotationEstimation = this.offerEstimations.find(offer => offer.type === OfferType.QUOTATION);
    }

    setTimeout(() => {
      const pricingContainer = document.getElementById(`pricing-container`);
      let maxHeight = 0;
      if (pricingContainer) {
        const alertDomElements: HTMLCollectionOf<HTMLElement> = pricingContainer.getElementsByClassName('alert') as HTMLCollectionOf<HTMLElement>;

        // get the max height
        for (let i = 0; i < alertDomElements.length; i++) {
          const alert = alertDomElements[i];
          const height = alert.clientHeight + 4;
          if (height > maxHeight) {
            maxHeight = height;
          }
        }

        // get the diff between max and actual height
        for (let i = 0; i < alertDomElements.length; i++) {
          const alert = alertDomElements[i];
          const height = alert.clientHeight + 4;
          if (height < maxHeight) {
            alert.style.marginTop = maxHeight - height + 'px';
          }
        }

        const offersValid = pricingContainer.getElementsByClassName('offer-valid') as HTMLCollectionOf<HTMLElement>;
        for (let i = 0; i < offersValid.length; i++) {
          const offer = offersValid[i];
          offer.style.marginTop = maxHeight + 8 + 'px';
        }
      }
    }, 100);
  }

  private removeConstraints(transport: Transport, offer: OfferEstimation): void {
    offer.constraints?.forEach(constraint => {
      switch (constraint.type) {
        case 'PICKUP_19t':
        case 'PICKUP_3500k':
        case 'PICKUP_7500k':
          transport.pickupAccessConstraint = AccessConstraintEnum.NONE;
          break;
        case 'PICKUP_crane':
        case 'PICKUP_dock':
        case 'PICKUP_hayon':
        case 'PICKUP_mounted_forklift':
        case 'PICKUP_ramp':
        case 'PICKUP_side':
        case 'PICKUP_top':
          transport.pickupLoadingConstraint = LoadingConstraintEnum.NONE;
          break;
        case 'DELIVERY_19t':
        case 'DELIVERY_3500k':
        case 'DELIVERY_7500k':
          transport.deliveryAccessConstraint = AccessConstraintEnum.NONE;
          break;
        case 'DELIVERY_crane':
        case 'DELIVERY_dock':
        case 'DELIVERY_hayon':
        case 'DELIVERY_mounted_forklift':
        case 'DELIVERY_ramp':
        case 'DELIVERY_side':
        case 'DELIVERY_top':
          transport.deliveryLoadingConstraint = LoadingConstraintEnum.NONE;
          break;
        case 'ESTIMATION_DELIVERY_TIMESLOT_INVALID':
          transport.deliveryTimeslotEnd = undefined;
          transport.deliveryTimeslotStart = undefined;
          break;
        case 'ESTIMATION_PICKUP_TIMESLOT_INVALID':
          transport.pickupTimeslotEnd = undefined;
          transport.pickupTimeslotStart = undefined;
          break;
      }
    });
  }

  public getStartHour(constraint: Constraint): string {
    const params = constraint.params as unknown as { startHour: string; endHour: string };
    return params.startHour;
  }

  public getEndHour(constraint: Constraint): string {
    const params = constraint.params as unknown as { startHour: string; endHour: string };
    return params.endHour;
  }
}
